import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { switchLanguagePath } from "../../i18n/routing";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { Menu as MenuIcon, Search, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

// Bandeiras como SVG inline — non dependen de emojis (que renderizan
// inconsistentemente entre OS) nin require ícono externo.
const FlagGalicia = ({ size = 20 }) => (
  <svg viewBox="0 0 30 20" width={size} height={(size * 2) / 3} className="rounded-sm border border-border/40 shadow-sm">
    <rect width="30" height="20" fill="white" />
    <path d="M0 20 L30 0" stroke="#4A90E2" strokeWidth="4" />
  </svg>
);
const FlagSpain = ({ size = 20 }) => (
  <svg viewBox="0 0 30 20" width={size} height={(size * 2) / 3} className="rounded-sm border border-border/40 shadow-sm">
    <rect width="30" height="5" fill="#AA151B" />
    <rect y="5" width="30" height="10" fill="#F1BF00" />
    <rect y="15" width="30" height="5" fill="#AA151B" />
  </svg>
);
const FlagPortugal = ({ size = 20 }) => (
  <svg viewBox="0 0 30 20" width={size} height={(size * 2) / 3} className="rounded-sm border border-border/40 shadow-sm">
    <rect width="12" height="20" fill="#006600" />
    <rect x="12" width="18" height="20" fill="#FF0000" />
  </svg>
);
const FlagUK = ({ size = 20 }) => (
  <svg viewBox="0 0 30 20" width={size} height={(size * 2) / 3} className="rounded-sm border border-border/40 shadow-sm">
    <rect width="30" height="20" fill="#012169" />
    <path d="M0 0 L30 20 M30 0 L0 20" stroke="white" strokeWidth="3" />
    <path d="M15 0 L15 20 M0 10 L30 10" stroke="white" strokeWidth="5" />
    <path d="M15 0 L15 20 M0 10 L30 10" stroke="#C8102E" strokeWidth="3" />
  </svg>
);

const LANGUAGES = [
  { code: "gl", label: "Galego",   flag: FlagGalicia,  available: true  },
  { code: "es", label: "Español",  flag: FlagSpain,    available: true  },
  { code: "pt", label: "Português", flag: FlagPortugal, available: false },
  { code: "en", label: "English",  flag: FlagUK,       available: false },
];

import { useAuth } from "../../context/AuthContext";
import { authService } from "../../api/services";
import useScrollY from "../../hooks/useScrollY";
import SearchBar from "../search/SearchBar";
import Avatar from "../ui/Avatar";
import NotificationsPanel from "../ui/NotificationsPanel";

export default function Header({ onOpenAuth }) {
  const { t, i18n } = useTranslation();
  const scrolled = useScrollY() > 80;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState({});

  // Cambia o idioma da UI + persiste. Garda en cookie/localStorage automaticamente
  // (configurado en src/i18n/index.js); se hai sesión iniciada, garda tamén no
  // perfil para que se vexa o mesmo idioma noutros dispositivos.
  async function handleLanguageChange(code) {
    if (code === i18n.language) return;
    await i18n.changeLanguage(code);
    // Sincroniza tamén a URL: se estamos en galego e activamos español, a
    // URL pasa a /es/...; e viceversa. Mantén querystring e hash.
    const newPath = switchLanguagePath(
      location.pathname + location.search + location.hash,
      code
    );
    if (newPath !== location.pathname + location.search + location.hash) {
      navigate(newPath, { replace: true });
    }
    if (user) {
      try {
        await authService.updateLanguage(code);
      } catch {
        // Falla silenciosa — o cambio xa funciona localmente; só perdemos
        // a sincronización entre dispositivos. Non bloquea ao usuario.
      }
    }
  }

  function handleSearch() {
    const typeRaw = searchValue.quantity?.listingType ?? "Ambos";
    const typeMap = { Maquinaria: "machine", Terras: "finca", Ambos: "" };
    const type = typeMap[typeRaw] ?? "";
    navigate(
      "/buscar?" +
        new URLSearchParams({
          comarca: searchValue.location ?? "",
          ...(searchValue.dateFrom && { dateFrom: searchValue.dateFrom }),
          ...(searchValue.dateTo   && { dateTo:   searchValue.dateTo }),
          ...(type                 && { type }),
        }).toString()
    );
    setSearchExpanded(false);
  }

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-all duration-200 ${
        scrolled ? "h-[72px] shadow-header" : "h-[80px]"
      }`}
    >
      <div className="max-w-[1760px] mx-auto px-4 sm:px-6 h-full flex items-center justify-between gap-2 sm:gap-4">
        {/* Left — Logo */}
        <div className="flex-shrink-0">
          <Link to="/" aria-label={t("header.logoHomeAria")} className="block">
            <img
              src="/logo.png"
              alt={t("header.logoAlt")}
              className="h-9 sm:h-12 md:h-14 lg:h-16 w-auto"
            />
          </Link>
        </div>

        {/* Center — Search */}
        <div className="hidden md:flex flex-1 justify-center max-w-2xl">
          {!scrolled || searchExpanded ? (
            <div className="w-full">
              <SearchBar value={searchValue} onChange={setSearchValue} onSearch={handleSearch} />
            </div>
          ) : (
            <button
              className="search-pill flex items-center gap-2 border border-border rounded-full px-4 py-2 shadow-card hover:shadow-card-hover transition-shadow text-sm text-dark"
              onClick={() => setSearchExpanded(true)}
              aria-label={t("header.search.openAria")}
            >
              <Search size={16} className="text-rauxa" />
              <span className="font-medium">{t("header.search.where")}</span>
              <span className="text-muted mx-1">·</span>
              <span className="text-muted">{t("header.search.when")}</span>
              <span className="text-muted mx-1">·</span>
              <span className="text-muted">{t("header.search.type")}</span>
            </button>
          )}
        </div>

        {/* Mobile — Search button (md:hidden). Compacto para deixar espazo
            ao chip Axudas + bell de notificacións + avatar á dereita. */}
        <div className="flex md:hidden flex-1 justify-center min-w-0">
          <button
            className="flex items-center gap-1.5 border border-border rounded-full px-3 py-1.5 shadow-card hover:shadow-card-hover transition-shadow text-sm text-dark w-full max-w-[130px]"
            onClick={() => navigate("/buscar")}
            aria-label={t("header.search.mobileButtonAria")}
          >
            <Search size={14} className="text-rauxa shrink-0" />
            <span className="font-medium truncate">{t("header.search.mobileButton")}</span>
          </button>
        </div>

        {/* Right — Controls */}
        <div className="flex-shrink-0 flex items-center gap-0.5 sm:gap-1">
          {/* "Publica o teu anuncio" — CTA primario (sólido) */}
          <button
            className="hidden lg:block rounded-full px-4 py-2 text-sm font-semibold bg-dark text-white hover:bg-black transition-colors whitespace-nowrap"
            onClick={() => navigate("/ser-propietario")}
          >
            {t("header.publishCta")}
          </button>

          {/* "Axudas" — chip secundario (ámbar) en md+, só emoji en móbil */}
          <button
            className="hidden md:inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors whitespace-nowrap"
            onClick={() => navigate("/axudas")}
          >
            <span aria-hidden>🌾</span>
            <span>{t("header.subsidies.label")}</span>
          </button>
          <button
            className="inline-flex md:hidden items-center justify-center w-9 h-9 rounded-full bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors text-base shrink-0"
            onClick={() => navigate("/axudas")}
            aria-label={t("header.subsidies.fullAria")}
            title={t("header.subsidies.fullAria")}
          >
            <span aria-hidden>🌾</span>
          </button>

          {/* Selector de idioma — oculto en móbil para liberar espazo
               (hai poucos móbiles de <380px onde cabe todo ademais da busca) */}
          <Menu as="div" className="relative hidden sm:block">
            <MenuButton
              className="rounded-full p-2 hover:bg-subtle transition-colors flex items-center"
              aria-label={t("header.language.selectAria")}
            >
              {(() => {
                // Mostra a bandeira do idioma actual. i18n.language pode vir
                // como "es-ES" → quedámonos coa raíz "es".
                const root = (i18n.language || "gl").split("-")[0];
                const current = LANGUAGES.find((l) => l.code === root) || LANGUAGES[0];
                const Flag = current.flag;
                return <Flag size={20} />;
              })()}
            </MenuButton>
            <MenuItems anchor="bottom end" className="mt-2 w-48 rounded-2xl bg-white shadow-xl border border-border focus:outline-none z-50 py-2">
              <div className="px-3 py-1.5 text-xs uppercase tracking-wide text-muted">{t("header.language.heading")}</div>
              {LANGUAGES.map((lang) => {
                const Flag = lang.flag;
                const isActive = (i18n.language || "gl").split("-")[0] === lang.code;
                return (
                  <MenuItem key={lang.code} disabled={!lang.available}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={lang.available ? () => handleLanguageChange(lang.code) : undefined}
                        className={`flex items-center gap-3 w-full px-3 py-2 text-sm ${
                          active ? "bg-subtle" : ""
                        } ${lang.available ? "text-dark" : "text-muted cursor-not-allowed"}`}
                        title={lang.available ? lang.label : t("header.language.comingSoon", { lang: lang.label })}
                      >
                        <Flag size={20} />
                        <span className="flex-1 text-left">{lang.label}</span>
                        {!lang.available
                          ? <Clock size={14} className="text-muted" />
                          : isActive
                            ? <span className="text-xs text-rauxa">●</span>
                            : null}
                      </button>
                    )}
                  </MenuItem>
                );
              })}
            </MenuItems>
          </Menu>

          {/* Notifications */}
          <NotificationsPanel />

          {/* Profile Menu */}
          <Menu as="div" className="relative">
            <MenuButton
              className="flex items-center gap-2 border border-border rounded-full p-1 pl-3 hover:shadow-card transition-shadow"
              aria-label={t("header.userMenu.ariaLabel")}
            >
              <MenuIcon size={16} className="text-dark" />
              <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                {user ? (
                  <Avatar user={user} size={32} />
                ) : (
                  <span className="text-white text-xs font-semibold select-none">
                    ?
                  </span>
                )}
              </div>
            </MenuButton>

            <MenuItems
              className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl shadow-card-hover border border-border py-2 z-50 animate-fade-in focus:outline-none"
              anchor="bottom end"
            >
              {/* Language picker — só en mobile (desktop xa ten o menú con
                  bandeira ó lado dereito do header). Un toque por bandeira
                  cambia o idioma sen pechar o menú. */}
              <div className="px-4 py-2 border-b border-border sm:hidden">
                <div className="text-[10px] uppercase tracking-wide text-muted mb-2">
                  {t("header.language.heading")}
                </div>
                <div className="flex gap-2">
                  {LANGUAGES.filter((l) => l.available).map((lang) => {
                    const Flag = lang.flag;
                    const isActive = (i18n.language || "gl").split("-")[0] === lang.code;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm border transition-colors ${
                          isActive
                            ? "border-dark bg-subtle font-semibold text-dark"
                            : "border-border text-dark hover:bg-subtle"
                        }`}
                      >
                        <Flag size={16} />
                        <span>{lang.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {user ? (
                <>
                  {/* User header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-border mb-1">
                    <Avatar user={user} size={36} />
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-dark truncate">
                        {user.name || user.email}
                      </span>
                      <span className="text-xs text-muted truncate">
                        {user.email}
                      </span>
                    </div>
                  </div>

                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => navigate("/perfil")}
                      >
                        {t("header.userMenu.profile")}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => navigate("/reservas")}
                      >
                        {t("header.userMenu.myRentals")}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => navigate("/propietario")}
                      >
                        {t("header.userMenu.myListings")}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => navigate("/propietario?tab=reservations")}
                      >
                        {t("header.userMenu.receivedReservations")}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => navigate("/favoritos")}
                      >
                        {t("header.userMenu.favorites")}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => navigate("/mensaxes")}
                      >
                        {t("header.userMenu.messages")}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => navigate("/registro-arrendamentos")}
                      >
                        {t("header.userMenu.incomeBook")}
                      </button>
                    )}
                  </MenuItem>

                  <div className="border-t border-border my-1" />

                  {user?.role === "ADMIN" && (
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          className={`flex items-center w-full px-4 py-3 text-sm font-semibold text-rauxa ${
                            focus ? "bg-subtle" : ""
                          } cursor-pointer`}
                          onClick={() => navigate("/admin")}
                        >
                          {t("header.userMenu.adminPanel")}
                        </button>
                      )}
                    </MenuItem>
                  )}

                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={logout}
                      >
                        {t("header.userMenu.logout")}
                      </button>
                    )}
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm font-semibold text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => onOpenAuth("login")}
                      >
                        {t("header.userMenu.signin")}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex items-center w-full px-4 py-3 text-sm text-dark ${
                          focus ? "bg-subtle" : ""
                        } cursor-pointer`}
                        onClick={() => onOpenAuth("register")}
                      >
                        {t("header.userMenu.register")}
                      </button>
                    )}
                  </MenuItem>
                </>
              )}
            </MenuItems>
          </Menu>
        </div>
      </div>
    </header>
  );
}
